import cta_1 from "@/assets/cta_1.jpg";
import section3_1 from "@/assets/section3_1.jpeg";
import section3_2 from "@/assets/section3_2.jpeg";
import section3_3 from "@/assets/section3_3.jpeg";
import HeroSection from "@/components/HeroSection";
import StarrySkyBackground from "@/components/StarrySkyBackground";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const features = [
  {
    title: "Personalized Characters",
    description:
      "Make your child the star of their story with custom characters that look just like them.",
    icon: "👤",
  },
  {
    title: "Custom Themes",
    description:
      "Choose from magical forests, space adventures, underwater worlds, and more.",
    icon: "🎨",
  },
  {
    title: "Multiple Art Styles",
    description:
      "Select from various artistic styles to match your child's preferences.",
    icon: "🖼️",
  },
  {
    title: "Save & Reread",
    description:
      "Keep your favorite stories in your library to enjoy again and again.",
    icon: "📚",
  },
  {
    title: "Age-Appropriate",
    description:
      "Stories tailored to your child's reading level and interests.",
    icon: "📖",
  },
  {
    title: "Moral Lessons",
    description: "Weave important life lessons into entertaining adventures.",
    icon: "🌟",
  },
];

const ageRanges = [
  {
    title: "Toddlers (0-3)",
    description: "Simpler, colorful, picture books.",
    imageAlt: "Toddler reading a picture book",
  },
  {
    title: "Preschool (3-5)",
    description:
      "Engaging stories with basic vocabulary and plenty of pictures",
    imageAlt: "Preschooler enjoying an illustrated story",
  },
  {
    title: "Early Readers (5-8+)",
    description:
      "Shorter, customizable stories for early and independent readers",
    imageAlt: "Child reading a chapter book",
  },
];

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/10 to-accent/20">
      <StarrySkyBackground
        showShootingStar={false}
        showAurora={true}
        showClouds={true}
      />

      {/* Hero Section */}
      <section className="relative pt-8">
        <div className="relative px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          {/* Trial Banner */}
          <Link to="/plans">
            <div className="text-center relative z-[1] md:z-0 lg:z-0 mb-8 bg-primary/90 py-3 rounded-lg text-slate-50 ">
              <p className="text-lg">
                Try WhimsyQuill free for 30 days!
                <Button
                  variant="link"
                  className="ml-2 text-lg underline text-slate-50"
                >
                  Start now →
                </Button>
              </p>
            </div>
          </Link>

          <div className="grid items-center grid-cols-1 lg:gap-12 lg:grid-cols-2">
            <div className="space-y-6 text-center lg:text-left">
              <HeroSection />
            </div>

            <motion.div
              className="relative"
              initial={{ opacity: 0, scale: 0.6, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{
                duration: 0.5,
                type: "spring",
                stiffness: 260,
                damping: 20,
              }}
            >
              <img
                src={cta_1}
                alt="Children reading a magical book with sparkles and characters coming to life"
                className="rounded-full shadow-[0_20px_50px_rgba(0,0,0,0.25)] transition-shadow duration-300 lg:max-w-full md:max-w-96 md:m-auto"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <h2 className="mb-12 text-3xl font-bold text-center md:text-4xl">
            Create Stories That Matter
          </h2>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {features.map((feature, index) => (
              <div
                key={index}
                className="p-6 transition-shadow duration-300 shadow-lg bg-card rounded-xl hover:shadow-xl"
              >
                <div className="flex items-center justify-center w-12 h-12 mb-4 rounded-full bg-primary/20">
                  {feature.icon}
                </div>
                <h3 className="mb-2 text-xl font-semibold">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Age Range Section */}
      <section className="py-20 bg-card">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <h2 className="mb-12 text-3xl font-bold text-center md:text-4xl">
            Perfect for Every Age
          </h2>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {ageRanges.map((range, index) => (
              <div
                key={index}
                className="p-6 text-center shadow-lg bg-background dark:bg-secondary rounded-xl"
              >
                <img
                  src={
                    index === 0
                      ? section3_1
                      : index === 1
                      ? section3_2
                      : section3_3
                  }
                  alt={range.imageAlt}
                  className="w-full mb-4 rounded-lg"
                />
                <h3 className="mb-2 text-xl font-semibold">{range.title}</h3>
                <p className="text-muted-foreground">{range.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl px-4 mx-auto text-center sm:px-6 lg:px-8">
          <h2 className="mb-6 text-3xl font-bold md:text-4xl">
            Start Your Story Today
          </h2>
          <p className="mb-8 text-xl text-muted-foreground">
            Try WhimsyQuill free for 30 days and create lasting stories for your
            little ones.
          </p>
          <Link to="/plans">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-3 font-medium text-black border rounded-lg bg-background border-primary dark:text-primary"
            >
              Begin Your Free Trial
            </motion.button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-card text-card-foreground">
        <div className="px-4 py-12 mx-auto max-w-7xl sm:px-6 md:py-16">
          <div className="flex flex-col items-center space-y-4">
            <ul className="flex flex-col items-center space-y-4 sm:flex-row sm:space-y-0 sm:space-x-8">
              <li>
                <a
                  href="/privacy.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-colors hover:text-primary"
                >
                  Privacy Policy
                </a>
              </li>
              <li>
                <a
                  href="/terms.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-colors hover:text-primary"
                >
                  Terms of Service
                </a>
              </li>
              <li>
                <a
                  href="mailto:support@whimsyquill.com"
                  className="transition-colors hover:text-primary"
                >
                  Contact Support
                </a>
              </li>
            </ul>
            <div className="w-full pt-4 border-t border-border">
              <p className="text-sm text-center text-muted-foreground sm:mt-4">
                © {new Date().getFullYear()} WhimsyQuill. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
